import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@/lib/api-client";

export type ResetScope = "all" | "seeds";

export interface ResetPreview {
  scope: ResetScope;
  hidden: number;
  kept: number;
  protected: Record<string, number>;
  seeds_demoted: number;
  seeds_skipped: number;
}

export interface ResetResult extends ResetPreview {
  reset_id: string;
}

/** Everything a reset or undo changes: the paper list (roles + dismissals),
 *  the concept (last_reset), the concept list counts, and FD staleness. */
function invalidateAfterReset(queryClient: ReturnType<typeof useQueryClient>, conceptId: string) {
  queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
  queryClient.invalidateQueries({ queryKey: ["concepts"] });
  queryClient.invalidateQueries({ queryKey: ["monitor-digest", conceptId] });
  queryClient.invalidateQueries({ queryKey: ["field-debate", conceptId] });
}

export function useResetPreview(conceptId: string, scope: ResetScope, enabled: boolean) {
  return useQuery<ResetPreview>({
    queryKey: ["concept-reset-preview", conceptId, scope],
    queryFn: () =>
      apiClient.get(`api/concepts/${conceptId}/reset/preview`, { searchParams: { scope } }).json(),
    enabled,
    // Counts must reflect the corpus at confirm time, never a cached view.
    staleTime: 0,
    gcTime: 0,
  });
}

/** Server-side bulk operation behind a confirm dialog — no optimistic cache
 *  write (the result depends on server-only protection rules); caches are
 *  invalidated once it settles. */
export function useResetConcept(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: ResetScope) =>
      apiClient
        .post(`api/concepts/${conceptId}/reset`, { json: { scope }, timeout: 120_000 })
        .json<ResetResult>(),
    onSettled: () => invalidateAfterReset(queryClient, conceptId),
  });
}

export function useUndoReset(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resetId: string) =>
      apiClient
        .post(`api/concepts/${conceptId}/reset/${resetId}/undo`, { timeout: 120_000 })
        .json<{ reset_id: string; unhidden: number; seeds_restored: number }>(),
    onSettled: () => invalidateAfterReset(queryClient, conceptId),
  });
}
